import { useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getSector, sectors, internationalCertifications, packagingInfo } from "@/data/products";
import { ArrowUpRight, Check, ShieldCheck, Ship, ChevronDown, ChevronUp, Package, Globe, Award } from "lucide-react";

export default function SectorDetail() {
  const { slug } = useParams();
  const sector = getSector(slug);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [showAllCerts, setShowAllCerts] = useState(false);
  const groupRefs = useRef({});

  const toggleGroup = (gi) => {
    const next = expandedGroup === gi ? null : gi;
    setExpandedGroup(next);
    if (next !== null) {
      // Wait for the previous group to collapse and the new one to start expanding,
      // then bring the just-opened group's header to a fixed offset from the top so
      // every group always opens "downward" from the same anchor point.
      setTimeout(() => {
        const el = groupRefs.current[gi];
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 110;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 320);
    }
  };

  if (!sector) {
    return (
      <main className="pt-32 pb-24 max-w-[1200px] mx-auto px-6 text-center" data-testid="sector-not-found">
        <h1 className="font-serif text-5xl">Sector not found</h1>
        <Link to="/catalog" className="mt-6 inline-block text-[#B85C38] underline">Back to catalog</Link>
      </main>
    );
  }

  const related = sectors.filter((s) => s.slug !== sector.slug).slice(0, 3);

  return (
    <main className="pb-24" data-testid="sector-detail-page">
      {/* Hero */}
      <section className="relative pt-36 pb-20 md:pb-28 overflow-hidden bg-[#0D1A12]">
        <div className="absolute inset-0">
          <img src={sector.hero} alt={sector.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A1B1A]/80 via-[#1A1B1A]/50 to-[#F9F8F6]" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 text-[#F9F8F6]/80 text-xs uppercase tracking-[0.3em]">
              <Link to="/catalog" className="hover:text-[#D4AF37]">Catalog</Link>
              <span>/</span>
              <span className="text-[#D4AF37]">Sector {sector.index}</span>
            </div>

            <h1 className="mt-6 font-serif text-6xl md:text-8xl font-light tracking-tighter leading-[0.95] text-[#F9F8F6] max-w-4xl">
              {sector.name}.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#F9F8F6]/80 font-light max-w-3xl leading-relaxed">{sector.blurb}</p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="sector-cta-quote" className="inline-flex items-center gap-3 rounded-full bg-[#D4AF37] text-[#1A1B1A] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#F9F8F6] transition-colors">
                Request a Quote <ArrowUpRight size={14} />
              </Link>
              <a href={`https://wa.me/917982650076?text=Hello%20AR%20Overseas%20-%20inquiry%20for%20${encodeURIComponent(sector.name)}`} target="_blank" rel="noreferrer" data-testid="sector-cta-whatsapp" className="inline-flex items-center gap-3 rounded-full border border-[#F9F8F6]/40 text-[#F9F8F6] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-white/10">
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>

          {/* floating stats */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
            <div className="glass rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#9F7A4A]">Countries Served</div>
              <div className="font-serif text-4xl text-[#2C4532] mt-2">{sector.stats.countries}</div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#9F7A4A]">Product Lines</div>
              <div className="font-serif text-4xl text-[#2C4532] mt-2">{sector.stats.products}</div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#9F7A4A]">Quote Response</div>
              <div className="font-serif text-4xl text-[#2C4532] mt-2">12h <span className="text-base text-[#4A4D4A]">guaranteed</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 mt-16">
        <div className="glass rounded-3xl border border-[#EAE6DF] p-8 md:p-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B85C38] mb-6">Why AR Overseas for {sector.name}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sector.highlights.map((h) => (
              <div key={h} className="flex items-start gap-4">
                <span className="mt-1 h-7 w-7 rounded-full bg-[#2C4532] text-[#D4AF37] flex items-center justify-center shrink-0"><Check size={14} /></span>
                <p className="text-base md:text-lg text-[#1A1B1A] font-light leading-relaxed">{h}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Varieties — Expandable */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 mt-24">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-3">Product Varieties</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-[#2C4532]">
            Complete Range
          </h2>
          <p className="mt-3 text-[#4A4D4A] font-light max-w-2xl">
            Click any product category below to explore full variety details, specifications and availability.
          </p>
        </div>

        <div className="space-y-4">
          {sector.groups.map((g, gi) => (
            <motion.div
              key={g.title}
              ref={(el) => (groupRefs.current[gi] = el)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-[#EAE6DF] bg-white overflow-hidden scroll-mt-24"
            >
              {/* Group Header — Clickable */}
              <button
                onClick={() => toggleGroup(gi)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-[#EAE6DF]/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] w-6">{String(gi + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-[#2C4532]">{g.title}</h3>
                    <p className="text-xs text-[#9F7A4A] mt-0.5">{g.items.length} varieties</p>
                  </div>
                </div>
                <span className="text-[#2C4532] flex-shrink-0 ml-4">
                  {expandedGroup === gi ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </span>
              </button>

              {/* Expanded Content */}
              <AnimatePresence>
                {expandedGroup === gi && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-[#EAE6DF] grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
                      {g.items.map((it) => (
                        <div key={it.name} className="rounded-xl border border-[#EAE6DF] bg-[#F9F8F6] p-5 hover:border-[#D4AF37] hover:-translate-y-0.5 transition-all duration-300">
                          <div className="font-serif text-lg text-[#1A1B1A]">{it.name}</div>
                          <p className="mt-2 text-sm text-[#4A4D4A] font-light leading-relaxed">{it.desc}</p>
                        </div>
                      ))}
                    </div>
                    <div className="p-6 pt-0">
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B85C38] hover:text-[#2C4532] transition-colors"
                      >
                        Enquire about {g.title} <ArrowUpRight size={12} />
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* International Certifications Required */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 mt-24">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-3">Export Compliance</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-[#2C4532]">
            International Certifications
          </h2>
          <p className="mt-3 text-[#4A4D4A] font-light max-w-2xl">
            All certifications and compliance documentation required to export to each major market — handled in-house by AR Overseas.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* EU */}
          <div className="rounded-2xl border border-[#EAE6DF] bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-full bg-[#003399]/10 flex items-center justify-center text-sm font-bold text-[#003399]">EU</div>
              <h3 className="font-serif text-xl text-[#2C4532]">European Union</h3>
            </div>
            <ul className="space-y-3">
              {internationalCertifications.eu.map((c) => (
                <li key={c.code} className="flex items-start gap-3">
                  <Award size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-medium text-[#2C4532] uppercase tracking-wide">{c.code}</span>
                    <p className="text-xs text-[#4A4D4A] font-light mt-0.5">{c.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* USA */}
          <div className="rounded-2xl border border-[#EAE6DF] bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-full bg-[#BF0A30]/10 flex items-center justify-center text-sm font-bold text-[#BF0A30]">US</div>
              <h3 className="font-serif text-xl text-[#2C4532]">USA & Canada</h3>
            </div>
            <ul className="space-y-3">
              {internationalCertifications.us.map((c) => (
                <li key={c.code} className="flex items-start gap-3">
                  <Award size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-medium text-[#2C4532] uppercase tracking-wide">{c.code}</span>
                    <p className="text-xs text-[#4A4D4A] font-light mt-0.5">{c.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* GCC */}
          <div className="rounded-2xl border border-[#EAE6DF] bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-full bg-[#009A44]/10 flex items-center justify-center text-sm font-bold text-[#009A44]">GCC</div>
              <h3 className="font-serif text-xl text-[#2C4532]">Gulf / Middle East</h3>
            </div>
            <ul className="space-y-3">
              {internationalCertifications.gcc.map((c) => (
                <li key={c.code} className="flex items-start gap-3">
                  <Award size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-medium text-[#2C4532] uppercase tracking-wide">{c.code}</span>
                    <p className="text-xs text-[#4A4D4A] font-light mt-0.5">{c.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Asia */}
          <div className="rounded-2xl border border-[#EAE6DF] bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-full bg-[#FF6600]/10 flex items-center justify-center text-sm font-bold text-[#FF6600]">AS</div>
              <h3 className="font-serif text-xl text-[#2C4532]">Asia Pacific</h3>
            </div>
            <ul className="space-y-3">
              {internationalCertifications.asia.map((c) => (
                <li key={c.code} className="flex items-start gap-3">
                  <Award size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-medium text-[#2C4532] uppercase tracking-wide">{c.code}</span>
                    <p className="text-xs text-[#4A4D4A] font-light mt-0.5">{c.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Restricted */}
          <div className="rounded-2xl border border-[#EAE6DF] bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-full bg-[#9F7A4A]/10 flex items-center justify-center">
                <ShieldCheck size={14} className="text-[#9F7A4A]" />
              </div>
              <h3 className="font-serif text-xl text-[#2C4532]">Regulated Items</h3>
            </div>
            <ul className="space-y-3">
              {internationalCertifications.restricted.map((c) => (
                <li key={c.code} className="flex items-start gap-3">
                  <Check size={14} className="text-[#2C4532] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-medium text-[#2C4532] uppercase tracking-wide">{c.code}</span>
                    <p className="text-xs text-[#4A4D4A] font-light mt-0.5">{c.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact for certs */}
          <div className="rounded-2xl border-2 border-dashed border-[#D4AF37] bg-[#2C4532]/5 p-6 flex flex-col justify-center text-center">
            <Globe size={32} className="text-[#D4AF37] mx-auto mb-3" />
            <p className="font-serif text-lg text-[#2C4532]">Need a specific certification?</p>
            <p className="text-xs text-[#4A4D4A] font-light mt-2 mb-4">We handle destination-specific documentation for 40+ markets. Contact us with your requirements.</p>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2C4532] text-[#F9F8F6] px-5 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#1F3124]">
              Ask Our Team <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* Packaging Requirements */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 mt-24">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-3">Packaging & Compliance</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-[#2C4532]">
            Market-Specific Packaging
          </h2>
          <p className="mt-3 text-[#4A4D4A] font-light max-w-2xl">
            Packaging requirements vary significantly by destination. AR Overseas handles all market-specific packaging compliance — including biodegradable options, language requirements and regulatory markings.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {packagingInfo.map((p) => (
            <div key={p.market} className="rounded-xl border border-[#EAE6DF] bg-white p-6">
              <div className="flex items-center gap-3 mb-3">
                <Package size={16} className="text-[#B85C38]" />
                <h4 className="font-serif text-lg text-[#2C4532]">{p.market}</h4>
              </div>
              <p className="text-sm text-[#4A4D4A] font-light leading-relaxed">{p.req}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process band */}
      <section className="mt-24 bg-[#2C4532] text-[#F9F8F6] py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-1">
              <ShieldCheck className="text-[#D4AF37] mb-4" size={32} />
              <h3 className="font-serif text-4xl md:text-5xl font-light tracking-tight">Export-ready in every shipment.</h3>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { t: "Documentation", d: "COO · Phytosanitary · COA · BL · Fumigation · MSDS — issued in destination language where required." },
                { t: "Packaging", d: "Jute, PP, vacuum, carton, jumbo bag, drum — private labelled in your brand identity. Biodegradable options for EU, UK and GCC." },
                { t: "Incoterms", d: "EXW, FOB, CFR, CIF, DDP — pricing with or without inland handling." },
                { t: "Ports", d: "Mumbai · JNPT · Mundra · Pipavav · Chennai · Kolkata · Kandla." },
              ].map((x) => (
                <div key={x.t}>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{x.t}</div>
                  <p className="mt-2 text-base opacity-85 font-light leading-relaxed">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 mt-24">
        <p className="text-xs uppercase tracking-[0.3em] text-[#9F7A4A] mb-6">Related Sectors</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {related.map((r) => (
            <Link key={r.slug} to={`/sectors/${r.slug}`} data-testid={`related-${r.slug}`} className="group relative aspect-[4/3] rounded-2xl overflow-hidden block">
              <img src={r.image} alt={r.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B1A]/80 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-[#F9F8F6]">
                <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">Sector {r.index}</div>
                <div className="font-serif text-2xl mt-1">{r.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
